import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import MarketingCreativeCard from './components/MarketingCreativeCard';
import MarketingCreativeModal from './components/MarketingCreativeModal';
import { useMobileSize } from '../../hooks/useScreenSize';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import { useNavigate } from 'react-router-dom';
import { useMarketingCreatives, fetchMarketingCreativeDetail } from './useMarketingCreatives';
import { Skeleton } from '@mui/material';
import toast from 'react-hot-toast';
import { useAnalytics } from '../../hooks/useAnalytics';

interface MarketingCreativesProps {}

interface Tag {
  title: string;
  color: string;
}

interface ThumbnailFormat {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string | null;
  size: number;
  width: number;
  height: number;
  sizeInBytes: number;
}

interface ThumbnailFormats {
  large?: ThumbnailFormat;
  medium?: ThumbnailFormat;
  small?: ThumbnailFormat;
  thumbnail?: ThumbnailFormat;
}

interface Thumbnail {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: ThumbnailFormats;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: any;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface CreativeCard {
  id?: string | number;
  documentId?: string;
  title: string;
  description: string;
  datePosted: string;
  tags: Tag[];
  views: string;
  image?: string;
  creativeType?: string;
  category?: string;
  sessionDetails?: any;
  contact?: any;
  actions?: string[];
  thumbnail?: Thumbnail;
}

const MarketingCreatives: React.FC<MarketingCreativesProps> = () => {
  const [selectedCard, setSelectedCard] = useState<CreativeCard | null>(null);
  const [viewCountOverride, setViewCountOverride] = useState<string | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const { data: marketingCreative, isLoading, error } = useMarketingCreatives();
  const { data } = useGetPartnerProfile();
  const navigate = useNavigate();
  const [marketingCreativeList, setMarketingCreativeList] = useState<any[]>([]);
  const [selectedCardSectionIdx, setSelectedCardSectionIdx] = useState<number | null>(null);
  const [selectedCardIdx, setSelectedCardIdx] = useState<number | null>(null);
  const { track } = useAnalytics();

  // Sync local list with hook data
  useEffect(() => {
    setMarketingCreativeList(marketingCreative || []);
  }, [marketingCreative]);

  const getBadgeColor = (color: string) => {
    return color === 'red' ? 'bg-red-600' : 'bg-green-600';
  };

  const captureRef = useRef<HTMLDivElement>(null);

  const handleCardClick = async (card: CreativeCard, sectionIdx?: number, cardIdx?: number) => {
    // Analytics: deal_creative_clicked
    track({
      eventName: 'deal_creative_clicked',
      eventProperties: {
        creative_id: card.id,
        creative_title: card.title,
        category: (card.category || '').toLowerCase(),
      },
    });
    setSelectedCard(card);
    setSelectedCardSectionIdx(typeof sectionIdx === 'number' ? sectionIdx : null);
    setSelectedCardIdx(typeof cardIdx === 'number' ? cardIdx : null);
    setViewCountOverride(null);
    setIsModalLoading(true);
    if (card.documentId) {
      fetchMarketingCreativeDetail(card.documentId)
        .then((detail) => {
          if (detail && (detail.viewCount || detail.views)) {
            setViewCountOverride(String(detail.viewCount || detail.views));
          }
        })
        .catch((error) => {
          console.error('Failed to fetch detailed card data:', error);
        })
        .finally(() => {
          setIsModalLoading(false);
        });
    } else {
      setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    // If viewCountOverride is set, update the card in marketingCreativeList
    if (
      selectedCard &&
      viewCountOverride !== null &&
      selectedCardSectionIdx !== null &&
      selectedCardIdx !== null
    ) {
      setMarketingCreativeList((prev) => {
        const updated = [...prev];
        const section = { ...updated[selectedCardSectionIdx] };
        const list = [...section.list];
        list[selectedCardIdx] = {
          ...list[selectedCardIdx],
          views: viewCountOverride,
        };
        section.list = list;
        updated[selectedCardSectionIdx] = section;
        return updated;
      });
    }
    setSelectedCard(null);
    setSelectedCardSectionIdx(null);
    setSelectedCardIdx(null);
    setViewCountOverride(null);
    setIsModalLoading(false);
  };

  const handleCopyToClipboard = async () => {
    // Analytics: creative_copy_clicked
    if (selectedCard) {
      track({
        eventName: 'creative_copy_clicked',
        eventProperties: {
          creative_id: selectedCard.id,
          creative_title: selectedCard.title,
          category: (selectedCard.category || '').toLowerCase(),
        },
      });
    }
    try {
      const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
      if (!referralCode) {
        toast.error('Referral code not available');
        return;
      }

      const referralLink = `https://partner.tapinvest.in/login?refCode=${referralCode}`;

      // Better HTML to text conversion that preserves formatting
      const cleanDescription = selectedCard?.description
        .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to line breaks
        .replace(/<\/p>/gi, '\n\n') // Convert </p> tags to double line breaks
        .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
        .replace(/<\/div>/gi, '\n') // Convert </div> tags to line breaks
        .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
        .replace(/<li[^>]*>/gi, '• ') // Convert <li> tags to bullet points
        .replace(/<\/li>/gi, '\n') // Convert </li> tags to line breaks
        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '*$1*') // Convert <strong> tags to bold formatting
        .replace(/<b[^>]*>(.*?)<\/b>/gi, '*$1*') // Convert <b> tags to bold formatting
        .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
        .replace(/&nbsp;/g, ' ') // Convert &nbsp; to regular spaces
        .replace(/&amp;/g, '&') // Convert &amp; to &
        .replace(/&lt;/g, '<') // Convert &lt; to <
        .replace(/&gt;/g, '>') // Convert &gt; to >
        .replace(/&quot;/g, '"') // Convert &quot; to "
        .replace(/&#39;/g, "'") // Convert &#39; to '
        .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double line breaks
        .trim();

      const textToCopy = `${selectedCard?.title}\n\n${cleanDescription}\n\nJoin here: ${referralLink}`;

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        toast.success('Content copied to clipboard!');
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          toast.success('Content copied to clipboard!');
        } catch (err) {
          console.error('Fallback copy failed:', err);
          toast.error('Failed to copy to clipboard');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleEmailShare = () => {
    // Analytics: creative_email_clicked
    if (selectedCard) {
      track({
        eventName: 'creative_email_clicked',
        eventProperties: {
          creative_id: selectedCard.id,
          creative_title: selectedCard.title,
          category: (selectedCard.category || '').toLowerCase(),
        },
      });
    }
    if (!selectedCard) return;

    const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
    if (!referralCode) {
      toast.error('Referral code not available');
      return;
    }

    const referralLink = `https://partner.tapinvest.in/login?refCode=${referralCode}`;

    // Better HTML to text conversion that preserves formatting for email
    const cleanDescription = selectedCard.description
      .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to line breaks
      .replace(/<\/p>/gi, '\n\n') // Convert </p> tags to double line breaks
      .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
      .replace(/<\/div>/gi, '\n') // Convert </div> tags to line breaks
      .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
      .replace(/<li[^>]*>/gi, '• ') // Convert <li> tags to bullet points
      .replace(/<\/li>/gi, '\n') // Convert </li> tags to line breaks
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '*$1*') // Convert <strong> tags to bold formatting
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '*$1*') // Convert <b> tags to bold formatting
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Convert &nbsp; to regular spaces
      .replace(/&amp;/g, '&') // Convert &amp; to &
      .replace(/&lt;/g, '<') // Convert &lt; to <
      .replace(/&gt;/g, '>') // Convert &gt; to >
      .replace(/&quot;/g, '"') // Convert &quot; to "
      .replace(/&#39;/g, "'") // Convert &#39; to '
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double line breaks
      .trim();

    const subject = encodeURIComponent(selectedCard.title);
    const body = encodeURIComponent(`${cleanDescription}\n\nJoin here: ${referralLink}`);

    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  const handleWhatsAppShare = () => {
    // Analytics: creative_whatsapp_clicked
    if (selectedCard) {
      track({
        eventName: 'creative_whatsapp_clicked',
        eventProperties: {
          creative_id: selectedCard.id,
          creative_title: selectedCard.title,
          category: (selectedCard.category || '').toLowerCase(),
        },
      });
    }
    if (!selectedCard) return;

    const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
    if (!referralCode) {
      toast.error('Referral code not available');
      return;
    }

    const referralLink = `https://partner.tapinvest.in/login?refCode=${referralCode}`;

    // Better HTML to text conversion that preserves formatting for WhatsApp
    const cleanDescription = selectedCard.description
      .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to line breaks
      .replace(/<\/p>/gi, '\n\n') // Convert </p> tags to double line breaks
      .replace(/<p[^>]*>/gi, '') // Remove opening <p> tags
      .replace(/<\/div>/gi, '\n') // Convert </div> tags to line breaks
      .replace(/<div[^>]*>/gi, '') // Remove opening <div> tags
      .replace(/<li[^>]*>/gi, '• ') // Convert <li> tags to bullet points
      .replace(/<\/li>/gi, '\n') // Convert </li> tags to line breaks
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '*$1*') // Convert <strong> tags to bold formatting
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '*$1*') // Convert <b> tags to bold formatting
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Convert &nbsp; to regular spaces
      .replace(/&amp;/g, '&') // Convert &amp; to &
      .replace(/&lt;/g, '<') // Convert &lt; to <
      .replace(/&gt;/g, '>') // Convert &gt; to >
      .replace(/&quot;/g, '"') // Convert &quot; to "
      .replace(/&#39;/g, "'") // Convert &#39; to '
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double line breaks
      .trim();

    const messageText = `${selectedCard.title}\n\n${cleanDescription}\n\nJoin here: ${referralLink}`;
    const message = encodeURIComponent(messageText);

    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleDownload = async () => {
    // Analytics: creative_download_clicked
    if (selectedCard) {
      track({
        eventName: 'creative_download_clicked',
        eventProperties: {
          creative_id: selectedCard.id,
          creative_title: selectedCard.title,
          category: (selectedCard.category || '').toLowerCase(),
        },
      });
    }
    if (!captureRef.current) {
      toast.error('Nothing to download.');
      return;
    }

    const convertImageToPng = async (imgSrc: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);

          try {
            const pngDataUrl = canvas.toDataURL('image/png');
            resolve(pngDataUrl);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imgSrc;
      });
    };

    try {
      // Convert the main image to PNG first
      const mainImg = captureRef.current.querySelector('img') as HTMLImageElement;
      if (mainImg && mainImg.src) {
        try {
          const pngDataUrl = await convertImageToPng(mainImg.src);
          // Replace the src temporarily for capture
          const originalSrc = mainImg.src;
          mainImg.src = pngDataUrl;

          // Wait a bit for the image to update
          await new Promise((resolve) => setTimeout(resolve, 100));
          const canvas = await html2canvas(captureRef.current, {
            allowTaint: true,
            useCORS: false,
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            width: 300,
            height: captureRef.current.offsetHeight,
          });

          // Restore original src
          mainImg.src = originalSrc;

          const dataUrl = canvas.toDataURL('image/png', 1.0);

          const link = document.createElement('a');
          link.download = `${selectedCard?.title?.replace(/[^a-z0-9]/gi, '_') || 'creative'}.png`;
          link.href = dataUrl;

          link.click();
          return;
        } catch (conversionError) {
          console.log('❌ Image conversion failed, trying direct capture:', conversionError);
        }
      }

      // Fallback: Direct capture with permissive settings
      const canvas = await html2canvas(captureRef.current, {
        allowTaint: true,
        useCORS: false,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        width: 300,
        height: captureRef.current.offsetHeight,
      });

      const dataUrl = canvas.toDataURL('image/png', 1.0);

      const link = document.createElement('a');
      link.download = `${selectedCard?.title?.replace(/[^a-z0-9]/gi, '_') || 'creative'}.png`;
      link.href = dataUrl;

      link.click();
    } catch (error) {
      toast.error(
        'Failed to download image. The image might be from an external source with restrictions.',
      );
    }
  };

  const isMobile = useMobileSize();

  const getSectionList = (section: any, idx: number) => {
    // Always show limited items since we're not expanding inline anymore
    const list = section?.list || [];
    return isMobile ? list.slice(0, 2) : list.slice(0, 4);
  };

  const handleViewAll = (sectionIdx: number, sectionTitle: string) => {
    // Navigate to a dedicated page with section info
    navigate(`/marketing-creatives/${sectionIdx}`, {
      state: { sectionTitle, sectionData: marketingCreative[sectionIdx] },
    });
  };

  return (
    <div className="flex flex-col max-w-7xl mx-auto px-4">
      <h1 className="font-medium text-4xl mb-9 mt-6">Marketing Creatives</h1>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-10 mb-10">
          {[1, 2].map((i) => (
            <div key={i} className="mb-10">
              <Skeleton variant="text" width={200} height={50} className="mb-8" />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2].map((j) => (
                  <Skeleton
                    key={j}
                    variant="rectangular"
                    width={254}
                    height={335}
                    className="rounded-lg"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Failed to load marketing creatives: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {!isLoading &&
        !error &&
        marketingCreativeList?.map((section: any, idx: number) => (
          <div key={idx} className="mb-10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{section.sectionTitle}</h2>
              {(section?.list?.length || 0) > 4 && (
                <button
                  className="bg-white shadow border border-slate-300 flex items-center gap-2 text-sm text-gray-800 rounded-lg font-semibold h-10 w-fit px-4"
                  onClick={() => handleViewAll(idx, section.sectionTitle)}
                >
                  View All
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
              {getSectionList(section, idx).map((card: CreativeCard, i: number) => (
                <MarketingCreativeCard
                  key={card.id || i}
                  card={card}
                  onClick={() => handleCardClick(card, idx, i)}
                />
              ))}
            </div>
          </div>
        ))}

      {/* Empty State */}
      {!isLoading && !error && (!marketingCreative || marketingCreative.length === 0) && (
        <div className="text-center py-8">
          <p className="text-gray-600">No marketing creatives available at the moment.</p>
        </div>
      )}

      {/* Modal */}
      {selectedCard && (
        <MarketingCreativeModal
          card={selectedCard}
          isModalLoading={isModalLoading}
          viewCountOverride={viewCountOverride}
          onClose={handleCloseModal}
          onWhatsAppShare={handleWhatsAppShare}
          onEmailShare={handleEmailShare}
          onCopyToClipboard={handleCopyToClipboard}
          onDownload={handleDownload}
          captureRef={captureRef}
          profileData={data?.data?.profile}
          referralCode={data?.data?.referralCodes?.[0]?.referralCode}
        />
      )}
    </div>
  );
};

MarketingCreatives.displayName = 'MarketingCreatives';
export default MarketingCreatives;
